import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import {
    ChapterContentDescription, ChapterPayload,
    Content, CourseContentsChapterWithProgress,
    GetContentPayload, GradeSurveyPayload, QuizResult,
    PageData,
    PageDataContents, ParticipantContentProgress, ParticipantCourseProgress, StartSurveyPayload,
    UserSelections, ContentType
} from '@/modules/learning/learning.model'
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
import Notification from '@/modules/common/services/notification.service'
import Container from 'typedi'
import { RawLocation, Route } from 'vue-router'
import { Media, fileTypeInfo } from '@/modules/common/models/media.model'
import { AxiosError } from 'axios'
import { SubmissionValidationError } from '@/bootstrap/global/errors'
import { GtrTimer, GtrMediaViewer } from '@/modules/common/components/ui-core/index.module'
import _ from 'lodash'

interface NavigationLink {
    text?: string;
    route?: RawLocation;
}

interface NavigationLinks {
    previous: NavigationLink;
    next: NavigationLink;
}

interface QuestionAnswers {
    question_id: string;
    question_text: string;
    user_answer: string[];
    correct_answer: string[];
    is_correct: boolean;
}

interface AssessmentStatistics {
    graded_questions: number;
    correct_answers: number;
    incorrect_answers: number;
    passed: boolean;
    score: number;
    question_answers: QuestionAnswers[];
}

Component.registerHooks([
    'beforeRouteLeave'
])

@Component({
    components: {
        GtrTimer,
        GtrMediaViewer
    },
    computed: {
        ...mapState('learning', ['assessment', 'lesson', 'current_chapter', 'loading_content', 'chapter_contents']),
        ...mapGetters('learning', ['availableContent', 'courseContentsWithProgress'] ),
        ...mapGetters('auth', ['participantCourseProgress'])
    },
    methods: {
        ...mapActions('learning', ['getContent', 'getChapterContent', 'startSurvey', 'submitSurveyAnswer', 'saveProgress', 'getAttempts']),
        ...mapMutations(
            'learning',
            {
                setContentLoading: 'SET_CONTENT_LOADING',
                setCurrentChapter: 'SET_CURRENT_CHAPTER',
                resetContent: 'RESET_CONTENT'
            }
        ),
        fileTypeInfo
    }
})
export default class ContentView extends Vue {
    @Prop({ required: true }) readonly course_uuid!: string;
    @Prop({ required: true }) readonly chapter_uuid!: string;
    @Prop({ required: true }) readonly content_type!: ContentType;
    @Prop({ required: true }) readonly content_id!: string;

    /*-- Vuex Bindings --*/
    getContent!: (payload: GetContentPayload) => Promise<any>
    getAttempts!: (payload: {
        course_uuid: string;
        content_id: string;
    }) => Promise<number>;
    getChapterContent!: (payload: ChapterPayload) => Promise<any>
    setContentLoading!: (payload: boolean) => void
    setCurrentChapter!: (payload: string) => void
    submitSurveyAnswer!: (payload: GradeSurveyPayload) => Promise<AssessmentStatistics>
    saveProgress!: (payload: GradeSurveyPayload) => Promise<void>
    startSurvey!: (payload: StartSurveyPayload) => Promise<any>
    resetContent!: () => void
    lesson!: Content;
    assessment!: Content;
    chapter_contents!: Array<ChapterContentDescription>
    courseContentsWithProgress!: Array<CourseContentsChapterWithProgress>
    loading_content!: boolean;
    participantCourseProgress!: ParticipantCourseProgress;

    /*-- State --*/
    score: number | null = null
    quiz_result: QuizResult | null = null
    timeElapsed = 0
    seconds = 0

    mediaViewer = {
        visible: false,
        url: ''
    }

    // state flags.
    submitted = false // quiz was submitted
    graded = false // quiz was graded
    passed: boolean | null = null // quiz passed or failed, or null if it hasn't been graded.
    paused = true // for timer

    // loading flags
    grading = false
    starting = false

    // dialog flags
    start = false // start dialog.
    scoreCard = false // scorecard at end.
    showDialog = false // show confirmation dialog.
    expiredAttempt = false

    textFieldMaxLength = 200
    user_responses: UserSelections | null = null
    currentQuestion = 1
    outOfTime = false
    totalAttempts: number | null = null

    assessmentStatistics: AssessmentStatistics | {} = {}

    // holder for promise resolver for confirmation dialog.
    private _dialogResolver: ((value: boolean) => void) | null = null;

    // region Computed Properties

    get content (): PageData {
        return (this.content_type === 'assessment' ? this.assessment : this.lesson)?.page_data ?? {}
    }

    get currentContentProgress (): ParticipantContentProgress {
        return this.participantCourseProgress['content_progress']?.[this.content_id] || {}
    }

    get attemptsLeft () {
        if (this.assessment && this.assessment.page_data.attempts_allowed) {
            return this.assessment.page_data.attempts_allowed - (this.totalAttempts ? this.totalAttempts : 0 )
        }
    }

    get assessmentDetails (): Record<string, any> | null {
        if (this.assessment) {
            const keys = ['time_limit', 'attempts_allowed', 'required_percentage', 'contents']
            const iconMap = {
                time_limit: 'mdi-clock-outline',
                attempts_allowed: 'mdi-sync',
                required_percentage: 'mdi-check-circle-outline',
                contents: 'mdi-help-circle-outline',
            }
            const { page_data } = this.assessment
            const details = {}
            for (const key of keys) {
                if (key in page_data && page_data[key] !== null && page_data[key] !== undefined) {
                    let value: any = null
                    switch (key) {
                        case 'time_limit':
                            value = `${page_data[key]} min`
                            break
                        case 'required_percentage':
                            value = `${page_data[key]?.toFixed(0)}%`
                            break
                        case 'attempts_allowed':
                            value = page_data[key]
                            break
                        case 'contents':
                            value = page_data[key].length
                            break
                    }

                   details[key] = {
                       icon: iconMap[key],
                       value
                   }
                }
            }

            return details
        } else {
            return null
        }
    }

    get displayAnswers () {
        if (this.content?.show_user_answers === 'none') {
            return false
        } else if (this.content?.show_user_answers === 'always') {
            return this.submitted
        } else {
            return this.submitted && this.passed
        }
    }

    get additionalMedia (): Media[] {
        return this.content?.additional_media?.map((url: string) => {
            return { url, ...fileTypeInfo(url) }
        }) ?? []
    }

    get questions (): PageDataContents[] {
        return this.content?.contents ?? []
    }

    get page_name (): string {
        if (this.content && (this.assessment || this.lesson)) {
            return this.content_type === 'assessment' ? this.assessment?.page_name : this.lesson?.page_name
        } else {
            return ''
        }
    }

    get canProceed (): boolean {
        if (this.content_type === 'lesson' && this.content?.primary_media) {
            const primaryMediaCategory = fileTypeInfo(this.content.primary_media).category
            if (primaryMediaCategory === 'video' || primaryMediaCategory === 'audio') {
                const requiredPercentage = this.content.required_percentage || 0
                const percentCompleted = this.currentContentProgress.percent_completed ?? 0
                return percentCompleted >= requiredPercentage
            }
        } else if (this.content_type === 'assessment') {
            return this.currentContentProgress.completed === true
        }
        return true
    }

    get navigationLinks () {
        const navigationLinks: NavigationLinks = {
            previous: {} as NavigationLink,
            next: {} as NavigationLink
        }

        const isContentAccessible = (content: any) => {
            return content.prerequisites.every(prereq => prereq === this.content_id || this.participantCourseProgress['content_progress']?.[prereq]?.completed)
        }

        const currentChapterIndex = this.courseContentsWithProgress.findIndex(chapter => chapter.uuid === this.chapter_uuid)
        if (currentChapterIndex === -1) { // Should never happen
            return navigationLinks
        }
        const currentChapter = this.courseContentsWithProgress[currentChapterIndex]
        const currentContentIndex = currentChapter.contents.findIndex(content => content.page_name === this.content_id)
        if (currentContentIndex === -1) { // Should never happen
            return navigationLinks
        }

        const previousContent = currentChapter.contents[currentContentIndex - 1]
        if (previousContent) {
            if (isContentAccessible(previousContent)) {
                navigationLinks.previous = {
                    text: 'Previous',
                    route: {
                        name: 'learning.content',
                        params: { content_type: previousContent.content_type, content_id: previousContent.page_name }
                    }
                }
            }
        } else {
            const previousChapter = this.courseContentsWithProgress[currentChapterIndex - 1]
            if (previousChapter) {
                navigationLinks.next = {
                    text: 'Previous Chapter',
                    route: {
                        name: 'chapter.view',
                        params: { chapter_uuid: previousChapter.uuid }
                    }
                }
            }
        }

        const nextContent = currentChapter.contents[currentContentIndex + 1]
        if (nextContent) {
            if (isContentAccessible(nextContent)) {
                navigationLinks.next = {
                    text: 'Next',
                    route: {
                        name: 'learning.content',
                        params: { content_type: nextContent.content_type, content_id: nextContent.page_name }
                    }
                }
            } else { // Content is locked, so just link to chapter view
                navigationLinks.next = {
                    text: 'Next',
                    route: {
                        name: 'chapter.view',
                        params: { chapter_uuid: this.chapter_uuid }
                    }
                }
            }
        } else {
            const nextChapter = this.courseContentsWithProgress[currentChapterIndex + 1]
            if (nextChapter) {
                navigationLinks.next = {
                    text: 'Next Chapter',
                    route: {
                        name: 'chapter.view',
                        params: { chapter_uuid: nextChapter.uuid }
                    }
                }
            } else {
                navigationLinks.next = {
                    text: 'Finished!',
                    route: { name: 'learning.dashboard' }
                }
            }
        }

        return navigationLinks
    }

    // #region Watchers

    @Watch('lesson', { deep: true })
    @Watch('assessment', { deep: true })
    onAssessmentChanged(assessment: Content) {
        const { contents, content_type } = assessment.page_data
        if (content_type === 'assessment') {
            const quiz = {}
            for (const question of contents) {
                if (question.type === 'checkbox') {
                    quiz[question.id] = []
                } else {
                    quiz[question.id] = null
                }
            }
            this.user_responses = quiz;
            this.seconds = this.assessment.page_data.time_limit ?  this.assessment.page_data.time_limit * 60 : 0
            this.submitted = false
        } else {
            this.user_responses = null
        }
        this.start = false
    }

    @Watch('$route')
    async onRouteChange ({ params }: Route) {
        try {
            this.setContentLoading(true)
            const { course_uuid, chapter_uuid, content_id } = params
            this.setCurrentChapter(chapter_uuid)
            await this.getContent({
                course_uuid,
                chapter_id: 'lms_chapter_' + chapter_uuid,
                content_id
            });
        } catch (error) {
            Container.get(Notification).error('Error fetching content')
        } finally {
            this.setContentLoading(false)
        }
    }

    // #region Lifecycle Hooks

    async mounted () {
        try {
            this.setContentLoading(true)
            if (!this.chapter_contents) {
                await this.getChapterContent({ course_uuid: this.course_uuid, chapter_uuid: this.chapter_uuid })
            }
            await this.getContent({
                course_uuid: this.course_uuid,
                chapter_id: 'lms_chapter_' + this.chapter_uuid,
                content_id: this.content_id
            });
            if (this.content_type === 'assessment') {
                this.totalAttempts = await this.getAttempts({
                    course_uuid: this.course_uuid,
                    content_id: this.content_id
                })
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                if (error.response && error.response.status === 404) {
                    Container.get(Notification).error(`Content "${this.content?.name}" not found.`)
                }
            } else {
                throw new Error(error);
            }
        } finally {
            this.setContentLoading(false)
        }
    }

    // #region Methods

    async onVideoTimeUpdate (currentTime: number, duration: number) {
        try {
            const percentCompleted = duration > 0 ? Math.round((currentTime / duration) * 100) : 0
            const participantProgress: ParticipantContentProgress = {
                primary_media_timestamp: currentTime,
                percent_completed: percentCompleted
            }
            await this.updateParticipantLessonProgress(participantProgress)
        } catch (error) {
            Container.get(Notification).error('Error updating video progress: ' + error)
        }
    }

    updateTimeElapsed (time: number) {
        this.timeElapsed = time
    }

    async waitForDialogConfirmation(): Promise<boolean> {
        this.showDialog = true;
        return new Promise<boolean>((resolve) => {
            this._dialogResolver = resolve;
        });
    }

    async onDialogConfirm() {
        this.showDialog = false;
        if (this._dialogResolver){
            const { assessment, user_responses: body, result_uuid } = this.validateSubmissionState();
            await this.saveProgress({
                course_uuid: this.course_uuid,
                chapter_uuid: this.chapter_uuid,
                result_uuid,
                assessment,
                body
            })
            this._dialogResolver(true);
            this._dialogResolver = null;
        }
    }

    onDialogCancel() {
        this.showDialog = false;
        if (this._dialogResolver) {
            this._dialogResolver(false);
            this._dialogResolver = null;
        }
    }

    validateSubmissionState() {
        if (!this.assessment) throw new SubmissionValidationError('Assessment is missing');
        if (!this.user_responses) throw new SubmissionValidationError('User responses are missing');
        if (!this.quiz_result) throw new SubmissionValidationError('Result is missing');
        return {
            result_uuid: this.quiz_result.uuid,
            assessment: this.assessment.page_name,
            user_responses: this.user_responses
        }
    }

    async onSubmit () {
        try {
            this.grading = true
            this.submitted = true
            this.paused = true
            const { assessment, user_responses: body, result_uuid } = this.validateSubmissionState();
            const response = await this.submitSurveyAnswer({
                course_uuid: this.course_uuid,
                chapter_uuid: this.chapter_uuid,
                result_uuid,
                assessment,
                body
            })
            this.score = response.score
            this.passed = response.passed
            this.assessmentStatistics = response
            this.graded = true
            this.scoreCard = true
            Container.get(Notification).success('Submitted')
            if (response.passed) {
                await this.$store.dispatch('auth/getParticipant', { event_identifier: this.course_uuid })
            }
        } catch (error) {
            this.handleError(error)
        } finally {
            this.grading = false
            window.scrollTo({ top: 0, left: 0 })
        }
    }

    async initialize () {
        try {
            if (this.quiz_result && this.user_responses) {
                if (Array.isArray(this.quiz_result.answers)) {
                    const oldResponses: UserSelections = {}
                    for (const key in this.user_responses) {
                        const answers = this.quiz_result.answers.filter(answer => answer.question_id === key)
                        const question = this.assessment.page_data.contents.find(question =>  question.id === key)
                        if (answers.length < 1) {
                           continue
                        }
                        if (question && question.type === 'checkbox') {
                            oldResponses[key] = answers.map(answer => answer.value)
                        } else {
                            const [answer] = answers
                            oldResponses[key] = answer.value
                        }
                    }
                    this.user_responses = _.merge(this.user_responses, oldResponses)
                }
            }

            // this happens last.
            this.start = true
            this.paused = false
        } catch (error) {
            Container.get(Notification).error('Error during quiz initialization')
        }
    }

    /**
     * handleError handles SubmissionValidationErrors and AxiosErrors from the BE.
     * @param error
     * @private
     */
    private handleError (error: any): void {
        const validationError = error instanceof SubmissionValidationError
        if (!(validationError)) {
            this.outOfTime = false
            this.graded = true
            this.passed = error.response.data.passed ?? false
            this.score = error.response.data.score || 0
            this.assessmentStatistics = error.response.data ?? null
            this.scoreCard = true
        }
        const message = validationError ? error.message : error.response.data.error_message
        if (message !== 'Bad Score') {
            Container.get(Notification).error(message)
        }
    }

    async timeLimitReachedHandler () {
        Container.get(Notification).sticky('Time is up! Submitting your quiz...', 'default')
        this.outOfTime = true
        await this.onSubmit()
    }

    async onStartQuiz () {
        try {
            window.scrollTo({ top: 0, left: 0 })
            this.starting = true
            const { attempt_details, time_left } = await this.startSurvey({ course_uuid: this.course_uuid, chapter_uuid: this.chapter_uuid, assessment: this.assessment.page_name })
            this.quiz_result = attempt_details
            this.seconds = time_left
            this.starting = false
            await this.initialize();
        } catch (e) {
            if (e instanceof AxiosError) {
                const message =  e.response?.data.error_message ?? 'Error while start Quiz'
                Container.get(Notification).error(message)
                this.starting = false
                if (e.response?.data.error_code === 'EXPIRED_QUIZ_ATTEMPT') {
                    this.passed = e.response?.data.passed ?? false
                    this.score = e.response?.data.score ?? 0
                    this.expiredAttempt = true
                    if (this.passed) {
                        await this.$store.dispatch('auth/getParticipant', { event_identifier: this.course_uuid })
                    }
                }
            } else {
                Container.get(Notification).error(`Error starting Quiz: ${e.message}`)
            }
        } finally {
            this.starting = false
        }
    }

    async onPrevious () {
        if (this.content_type === 'assessment' && !this.scoreCard && (this.start && !this.outOfTime)) {
            const confirmed = await this.waitForDialogConfirmation()
            if (confirmed) {
                if (this.navigationLinks.previous.route) {
                    this.scoreCard = false
                    await this.$router.push(this.navigationLinks.previous.route)
                }
            }
        } else {
            if (this.navigationLinks.previous.route) {
                this.scoreCard = false
                await this.$router.push(this.navigationLinks.previous.route)
            }
        }
        window.scrollTo({ top: 0, left: 0 })
    }

    async onNext () {
        if (this.content_type === 'lesson') {
            try {
                await this.updateParticipantLessonProgress({
                    completed: true
                })
            } catch (error) {
                Container.get(Notification).error('Please complete the lesson before continuing.')
                return
            }
        }
        if (this.navigationLinks.next.route) {
            this.scoreCard = false
            const route: RawLocation = this.navigationLinks.next.route
            if (typeof route === 'object' && 'name' in route && route.name === 'learning.dashboard') {
                await this.$store.dispatch('auth/getParticipant', { event_identifier: this.course_uuid })
            }
            await this.$router.push(this.navigationLinks.next.route)
        }
        window.scrollTo({ top: 0, left: 0 })
    }

    async updateParticipantLessonProgress (contentProgress: ParticipantContentProgress){
        await this.$store.dispatch('auth/updateParticipantLessonProgress', {
            course_uuid: this.course_uuid,
            content_id: this.content_id,
            content_progress: contentProgress
        })
    }

    async beforeRouteLeave (to: Route, from: Route, next: (arg?: any) => void): Promise<void> {
        let confirmed = true

        if (!this.graded && (this.start && !this.outOfTime)) {
            confirmed = await this.waitForDialogConfirmation()
        }

        if (confirmed) {
            if (to.name === 'chapter.view' || to.name === 'learning.dashboard') {
                this.resetContent()
                return next()
            }
            next()
        }
    }

    async goToChapterContent () {
        await this.$router.push({ name: 'chapter.view', params: { chapter_uuid: this.chapter_uuid } })
    }

    ratedQuestion (range: { start: number; other: Array<any>; end: number }): Array<string> {
        const { start, end } = range
        const result: Array<string> = [];
        for (let x = start; x <= end; x++) {
            result.push(x.toString())
        }
        return result
    }

    openMediaViewer (url: string) {
        this.mediaViewer.url = url
        this.mediaViewer.visible = true
    }

    additionalMediaAction (media: Media) {
        if (media.category === 'video') {
            this.openMediaViewer(media.url)
        } else if (media.category === 'document') {
            window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(media.url)}`, '_blank')
        } else {
            window.open(media.url, '_blank')
        }
    }

    handleRetakeQuiz () {
        location.reload()
    }
}
